import * as THREE from 'three';

/**
 * A floating HP bar above a monster — user-requested ("it is not clear how
 * quickly I can kill them"). Two flat-colored sprites (dark background +
 * colored fill); `THREE.Sprite` always faces the camera on its own (no
 * manual billboard/look-at math needed), and `SpriteMaterial` renders a
 * solid color rectangle when given no texture `map`, so this needs no
 * canvas/texture management at all. Deliberately monster-only per the
 * request — players read their own health via the existing debug HUD.
 */
const WIDTH_M = 0.6;
const HEIGHT_M = 0.08;

export class HealthBarSprite {
  readonly group = new THREE.Group();
  private readonly fill: THREE.Sprite;
  private readonly fillMaterial: THREE.SpriteMaterial;
  private readonly bgMaterial: THREE.SpriteMaterial;

  constructor() {
    this.bgMaterial = new THREE.SpriteMaterial({ color: 0x1a0000, depthTest: false, sizeAttenuation: true });
    const bg = new THREE.Sprite(this.bgMaterial);
    bg.scale.set(WIDTH_M, HEIGHT_M, 1);

    this.fillMaterial = new THREE.SpriteMaterial({ color: 0x33cc55, depthTest: false, sizeAttenuation: true });
    this.fill = new THREE.Sprite(this.fillMaterial);
    this.fill.scale.set(WIDTH_M, HEIGHT_M, 1);
    this.fill.position.z = 0.001; // avoid z-fighting with the background sprite

    // Render after the level geometry regardless of depth, so the bar never
    // gets fully lost behind a wall corner clipping through it — legibility
    // over strict occlusion realism for a live "can I kill this" readout.
    bg.renderOrder = 10;
    this.fill.renderOrder = 11;

    this.group.add(bg, this.fill);
    this.setFraction(1);
  }

  /** Updates the fill's width and left-anchors it, so it drains right-to-left as HP drops. */
  setFraction(fraction01: number): void {
    const clamped = Math.min(1, Math.max(0, fraction01));
    const width = WIDTH_M * clamped;
    this.fill.scale.x = width;
    this.fill.position.x = -WIDTH_M / 2 + width / 2;
    this.fillMaterial.color.setHex(clamped > 0.5 ? 0x33cc55 : clamped > 0.25 ? 0xddaa22 : 0xdd3333);
    this.group.visible = clamped > 0; // hide entirely on death rather than show an empty husk
  }

  dispose(): void {
    this.group.removeFromParent();
    this.bgMaterial.dispose();
    this.fillMaterial.dispose();
  }
}
